import { Box, Stack, ToggleButton, ToggleButtonGroup, Tooltip } from '@chronosphereio/chrono-ui';
import { accessStorage, FeatureFlagGate, useFeatureFlags, useIsInternalUser } from '@chronosphereio/core';
import { PromQLEditor, type VariableCompletion } from '@chronosphereio/editors/prometheus';
import type { ReactCodeMirrorRef } from '@uiw/react-codemirror';
import { InformationOutline as InfoIcon } from 'mdi-material-ui';
import { ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';
import { useOptionalTemplateVariableDefinitions } from '@/dashboards/context';
import { useAnalytics, useAppMetadataContext, useTimeRange, useVizFrameworkConfig } from '@/plugin-system';
import {
  QueryAssistant,
  QueryAssistantAcceptAndReject,
  QueryAssistantButton,
  QueryAssistantProvider,
  useIsQueryAssistantEnabled,
  useQueryAssistant,
  useQueryAssistantAcceptAndReject,
  useQueryAssistantShortcut,
} from '@/prometheus-plugin/ai';
import { injectDownsamplingStrategy as injectDownsamplingStrategy } from '@/prometheus-plugin/utils';
import { DEFAULT_PROM_URL } from '../../model';
import { PrometheusOptionsEditor } from './PrometheusOptionsEditor';
import { PromQLVariableSuggestion } from './PrometheusVariableSuggestion';
import { PromQLBuilder } from './promql-builder';
import { PromQLMode, PromQLModeProvider, usePromQLModeContext } from './promql-builder/promql-builder-context';
import { isQuerySupportedByBuilder } from './promql-builder/promql-builder-model';
import { EditorProvider, PrometheusTimeSeriesQueryEditorProps, useEditorState } from './query-editor-model';

type CodeBuilderToggleProps = {
  query: string;
  mode: PromQLMode;
  onModeChange: (mode: PromQLMode) => void;
  /**
   * Identifies where the toggle is rendered, for analytics tracking.
   */
  source?: 'query_editor' | 'series_selector';
};

/**
 * Toggle between Code and Builder modes for PromQL editing.
 * Warns the user when switching to builder if the query uses unsupported features.
 */
export function CodeBuilderToggle({ query, mode, onModeChange, source }: CodeBuilderToggleProps) {
  const analytics = useAnalytics();
  return (
    <ToggleButtonGroup
      value={mode}
      onChange={(_, newMode) => {
        if (newMode === null) return;
        if (newMode === 'builder') {
          const querySupported = isQuerySupportedByBuilder(query);
          let confirmed = true;
          if (!querySupported) {
            confirmed = window.confirm(
              'This query contains features not supported in builder mode. You may lose parts of your query. Would you like to continue?'
            );
          }
          analytics?.track('prometheus_promql_builder_clicked', {
            source,
            querySupported,
            confirmed,
          });
          if (!confirmed) return;
        }
        onModeChange(newMode);
      }}
    >
      <ToggleButton sx={{ height: 24, fontSize: 'small' }} value="code">
        Code
      </ToggleButton>
      <ToggleButton sx={{ height: 24, fontSize: 'small', paddingY: 0 }} value="builder">
        Builder
      </ToggleButton>
    </ToggleButtonGroup>
  );
}

/**
 * The options editor component for editing a PrometheusTimeSeriesQuery's spec.
 */
export function PrometheusTimeSeriesQueryEditor(props: PrometheusTimeSeriesQueryEditorProps) {
  // Only show variable suggestion on dashboards, not Metrics Explorer or other pages.
  const ctx = useAppMetadataContext();
  const showVariableSuggestion = ctx?.app === 'native-dashboard';
  return (
    <EditorProvider {...props}>
      <ModeWrapper>
        <QueryAssistantWrapper>
          <PrometheusTimeSeriesQueryEditorBase {...props} showVariableSuggestion={showVariableSuggestion} />
        </QueryAssistantWrapper>
      </ModeWrapper>
    </EditorProvider>
  );
}

function ModeWrapper({ children }: { children: ReactNode }) {
  const { enablePromQLQueryBuilder: enableBuilder } = useFeatureFlags();
  const { spec } = useEditorState();
  const isInternalUser = useIsInternalUser();

  // We use accessStorage because we don't want to trigger a re-render
  // when we update the stored value.
  // `mode` should be owned by the PromQLModeProvider.
  const { getItem: getLastUsedMode, setItem: setLastUsedMode } = accessStorage<PromQLMode>(
    window.localStorage,
    'last-used-prom-query-editor-mode'
  );

  const lastUsedMode = getLastUsedMode();
  let initialMode: PromQLMode = 'code';
  if (enableBuilder) {
    if (isInternalUser && spec.query === '') {
      initialMode = lastUsedMode ?? 'builder';
    }
  }

  return (
    <PromQLModeProvider initialMode={initialMode} setLastUsedMode={setLastUsedMode}>
      {children}
    </PromQLModeProvider>
  );
}

function QueryAssistantWrapper({ children }: { children: ReactNode }) {
  const { spec, handleChange } = useEditorState();
  const { setMode } = usePromQLModeContext();

  // Used to update the query.
  const onChange = (query: string) => handleChange(query, { shouldTriggerOnChange: true });

  // Used to update and run the query.
  const onRun = () => handleChange(spec.query, { shouldRun: true });

  // When opening the query generation interface, switch to code mode.
  const onOpen = () => setMode('code');

  return (
    <QueryAssistantProvider query={spec.query} onChange={onChange} onRun={onRun} onOpen={onOpen}>
      {children}
    </QueryAssistantProvider>
  );
}

/**
 * The options editor component for editing a PrometheusTimeSeriesQuery's spec.
 */
function PrometheusTimeSeriesQueryEditorBase(props: PrometheusTimeSeriesQueryEditorProps) {
  const { showVariableSuggestion = false, onChange, variableOpportunities = [] } = props;
  const { spec, triggerOnChange, handleChange } = useEditorState();
  const { absoluteTimeRange } = useTimeRange();
  const { isAssistantOpen, suggestion, committedQuery } = useQueryAssistant();

  const queryRewrite = useMemo(() => {
    if (!spec.downsampling_strategy || !spec.query) {
      return null;
    }
    return injectDownsamplingStrategy(spec.query, spec.downsampling_strategy);
  }, [spec.downsampling_strategy, spec.query]);

  const handleQueryChange = useCallback(
    (e: string) => {
      handleChange(e);
    },
    [handleChange]
  );

  const config = useVizFrameworkConfig();
  const variableDefinitions = useOptionalTemplateVariableDefinitions();
  const variables = useMemo<VariableCompletion[]>(
    () =>
      variableDefinitions.map((definition) => {
        if (definition.kind === 'IntervalVariable') {
          return { name: definition.spec.name, kind: 'interval' };
        }

        if (definition.kind === 'ListVariable' && definition.spec.plugin.kind === 'PrometheusLabelValuesVariable') {
          return {
            name: definition.spec.name,
            kind: 'label',
            labelName: definition.spec.plugin.spec.label_name,
          };
        }

        return { name: definition.spec.name, kind: 'text' };
      }),
    [variableDefinitions]
  );

  // When `enablePromQLQueryBuilder` is true, user can switch between editor and builder.
  // When false, user only sees the editor.
  const Builder = <PromQLBuilder value={spec.query} onChange={handleChange} />;

  const isAssistantEnabled = useIsQueryAssistantEnabled();
  const placeholderText = isAssistantEnabled
    ? 'Enter a PromQL query, or Cmd+I to edit with AI'
    : 'Enter a PromQL query';

  const editorRef = useRef<ReactCodeMirrorRef>(null);

  // When the assistant is closed, re-focus on the editor.
  useEffect(() => {
    if (!isAssistantOpen) {
      editorRef.current?.view?.focus();
    }
  }, [isAssistantOpen]);

  const Editor = (
    <PromQLEditor
      ref={editorRef}
      // eslint-disable-next-line jsx-a11y/no-autofocus
      autoFocus={!isAssistantOpen}
      disableBorder
      placeholder={placeholderText}
      data-testid="promQLEditor"
      queryApiEndpoint={DEFAULT_PROM_URL}
      value={spec.query}
      previousValue={isAssistantOpen && suggestion ? committedQuery : undefined}
      onChange={handleQueryChange}
      onRun={triggerOnChange}
      onBlur={triggerOnChange}
      timeRange={absoluteTimeRange}
      enableBuiltInVariables={config?.enableBuiltInVariables}
      variables={variables}
    />
  );

  return (
    <Stack spacing={0.5}>
      <PromQLEditorAndBuilder Editor={Editor} Builder={Builder} />

      <FeatureFlagGate condition={() => config?.enableExperimentalPrometheusDownsampling === true}>
        {queryRewrite && spec.downsampling_strategy !== 'LTTB' && (
          <Box sx={{ opacity: 0.7, fontSize: (t) => t.typography.body2 }}>
            <Stack direction={'row'} spacing={0.5} alignItems={'center'}>
              <Tooltip Content="The original query has been rewritten to apply the downsample strategy. This can be disabled by setting the downsample strategy to 'Off'">
                <InfoIcon fontSize="inherit" color="secondary" />
              </Tooltip>
              <span>Resolved PromQL: </span>{' '}
              <PromQLEditor enableLinter={false} readOnly disableBorder value={queryRewrite} />
            </Stack>
          </Box>
        )}
      </FeatureFlagGate>

      {showVariableSuggestion && (
        <PromQLVariableSuggestion
          query={spec.query}
          onUpdateQuery={handleChange}
          variableOpportunities={variableOpportunities}
        />
      )}

      <PrometheusOptionsEditor spec={spec} onChange={onChange} />
    </Stack>
  );
}

function PromQLEditorAndBuilder({ Editor, Builder }: { Editor: ReactNode; Builder: ReactNode }) {
  const { spec, handleChange } = useEditorState();
  const { mode, setMode } = usePromQLModeContext();
  const config = useVizFrameworkConfig();
  const analytics = useAnalytics();

  useEffect(() => {
    analytics?.track('prometheus_query_editor_view', { mode });
    // Only track when mode changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const PrometheusQueryEditorToolbar = config?.PrometheusQueryEditorToolbar;

  const { enablePromQLQueryBuilder } = useFeatureFlags();
  const isAssistantEnabled = useIsQueryAssistantEnabled();
  const { isAssistantOpen, suggestion, closeAssistant } = useQueryAssistant();
  const { containerRef } = useQueryAssistantShortcut();
  const { onAccept, onReject } = useQueryAssistantAcceptAndReject({
    feature: 'promql_query_generation',
    evalName: 'user_acceptance',
  });

  return (
    <Stack
      ref={containerRef}
      border="1px solid"
      borderColor={(theme) => theme.palette.border.separator}
      borderRadius={(theme) => (theme.featureFlags.panwRebrandStubYear ? theme.shape.borderRadiusMd : 1)}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="space-between"
        p={1}
        bgcolor="background.paper"
        borderRadius={(theme) => (theme.featureFlags.panwRebrandStubYear ? theme.shape.borderRadiusMd : undefined)}
      >
        {enablePromQLQueryBuilder ? (
          <CodeBuilderToggle
            query={spec.query}
            mode={mode}
            source="query_editor"
            onModeChange={(newMode) => {
              if (newMode === 'builder') closeAssistant();
              setMode(newMode);
            }}
          />
        ) : null}
        {PrometheusQueryEditorToolbar && (
          <PrometheusQueryEditorToolbar query={spec.query} onUpdateQuery={handleChange} mode={mode} />
        )}
      </Stack>

      <Stack gap={1} padding={1}>
        <Stack direction="row" alignItems="start" spacing={1} justifyContent="space-between">
          <Box flexGrow={1}>{mode === 'code' ? Editor : Builder}</Box>
          {isAssistantEnabled && (!isAssistantOpen || mode === 'builder') && <QueryAssistantButton />}
        </Stack>

        {mode === 'code' && isAssistantOpen && suggestion && (
          <QueryAssistantAcceptAndReject onAccept={onAccept} onReject={onReject} />
        )}

        {mode === 'code' && isAssistantOpen && <QueryAssistant />}
      </Stack>
    </Stack>
  );
}
